import StringListEditor from "./StringListEditor";
import { QuestionTypeEditorProps } from "./types";

// drag_drop_ordering: optionsJson { items[], slots[] },
// correctAnswerJson { [slot]: item }. Items/slots are tracked by value, so when
// one is renamed we carry the saved correct order across by position.
const OrderingEditor = ({
  optionsJson,
  correctAnswerJson,
  onChange,
}: QuestionTypeEditorProps) => {
  const items = (optionsJson.items as string[]) ?? [];
  const slots = (optionsJson.slots as string[]) ?? [];
  const answer = correctAnswerJson as Record<string, string>;

  // Keep only answers whose slot + item still exist.
  const prune = (
    nextItems: string[],
    nextSlots: string[],
    nextAnswer: Record<string, string>
  ): Record<string, string> => {
    const cleaned: Record<string, string> = {};
    nextSlots.forEach((slot) => {
      if (slot && nextItems.includes(nextAnswer[slot])) {
        cleaned[slot] = nextAnswer[slot];
      }
    });
    return cleaned;
  };

  const setItems = (next: string[]) => {
    // On a rename (same length) map old item text -> new so answers follow it.
    const rename: Record<string, string> = {};
    if (next.length === items.length) {
      items.forEach((old, i) => {
        if (old !== next[i]) rename[old] = next[i];
      });
    }
    const movedAnswer: Record<string, string> = {};
    slots.forEach((slot) => {
      const v = answer[slot];
      if (v !== undefined) movedAnswer[slot] = rename[v] ?? v;
    });
    onChange({
      optionsJson: { items: next, slots },
      correctAnswerJson: prune(next, slots, movedAnswer),
    });
  };

  const setSlots = (next: string[]) => {
    // On a rename (same length) move the answer to the renamed slot key.
    let movedAnswer = answer;
    if (next.length === slots.length) {
      movedAnswer = {};
      slots.forEach((old, i) => {
        if (answer[old] !== undefined) movedAnswer[next[i]] = answer[old];
      });
    }
    onChange({
      optionsJson: { items, slots: next },
      correctAnswerJson: prune(items, next, movedAnswer),
    });
  };

  const assign = (slot: string, item: string) =>
    onChange({
      optionsJson: { items, slots },
      correctAnswerJson: { ...answer, [slot]: item },
    });

  return (
    <div className="space-y-4">
      <StringListEditor
        label="Items (the options to order)"
        values={items}
        onChange={setItems}
        placeholder="e.g. Forecast sales"
        addLabel="Add item"
      />
      <StringListEditor
        label="Slots (ordered positions)"
        values={slots}
        onChange={setSlots}
        placeholder="e.g. First"
        addLabel="Add slot"
      />
      {slots.filter(Boolean).length > 0 && (
        <div className="space-y-2">
          <label className="block font-medium text-burntBlack text-sm">
            Correct order
          </label>
          {slots.map((slot, si) =>
            slot ? (
              <div key={si} className="flex items-center gap-3">
                <span className="w-28 text-sm text-gray-600">{slot}</span>
                <select
                  value={answer[slot] ?? ""}
                  onChange={(e) => assign(slot, e.target.value)}
                  className="flex-1 h-10 px-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                >
                  <option value="">— select item —</option>
                  {items.map((item, ii) =>
                    item ? (
                      <option key={ii} value={item}>
                        {item}
                      </option>
                    ) : null
                  )}
                </select>
              </div>
            ) : null
          )}
        </div>
      )}
    </div>
  );
};

export default OrderingEditor;
